import { openContactEmail, openContactCall } from "../utils/contactCommunication";
import { resolveMergedCount, subscribeCompanyRelationshipsChanged } from "../utils/companyRelationshipCounts";
import useProfileTab from "../hooks/useProfileTab";
// src/components/LeadTabsLayout.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Box, Card } from "@mui/material";
import LeaderboardOutlinedIcon from "@mui/icons-material/LeaderboardOutlined";
import { useLocation, useNavigate } from "react-router-dom";

import Breadcrumb from "../components/Breadcrumb";
import StatsSlider from "./StatsSlider";
import CompanyProfileHeader from "./CompanyProfileHeader";
import EntityBackgroundTasks from "./EntityBackgroundTasks";
import { syncCompanyBackgroundWork } from "../utils/entityBackgroundWorkState";
import ListTableTabsTemplate from "./layouts/ListTableTabsTemplate";
import {
  TAB_VIEW_CONTENT_WRAPPER_SX,
  TAB_VIEW_HEADER_SX,
  TAB_VIEW_SIDE_PADDING_X,
} from "./tabViewContentSpacing";

import CompanyDetailsTabContent from "./LeadTabsContent/CompanyDetailsTabContent";
import CompanyContactsTabContent from "./LeadTabsContent/CompanyContactsTabContent";
import CompanyJobsTabContent from "./LeadTabsContent/CompanyJobsTabContent";
import CompanyCandidatesTabContent from "./LeadTabsContent/CompanyCandidatesTabContent";
import CompanyLeadsSection from "./LeadTabsContent/CompanyLeadsSection";
import CompanyHeader from "./LeadTabsContent/DetailSections/CompanyHeader";
import LeadActivitiesTabContent from "./LeadActivitiesTabContent";
import AddJobDialog from "./PopupWindows/AddJobDialog";
import AddToSequenceDialog from "./PopupWindows/AddToSequenceDialog";
import AIAgentDialog from "./PopupWindows/AIAgentDialog";
import LeadDescriptionDialog from "./PopupWindows/LeadDescriptionDialog";
import QuickActionsCard from "./sidebar-widgets/QuickActionsCard";
import AIAgentCard from "./sidebar-widgets/AIAgentCard";
import FundingFinancialsCard from "./sidebar-widgets/FundingFinancialsCard";
import HiringTeamCard from "./sidebar-widgets/HiringTeamCard";
import ProposalIcon from "./icons/ProposalIcon";
import { Briefcase, Call, Sms } from "iconsax-react";
import {
  COMPANY_STATUS_OPTIONS,
  normalizeCompanyStatusLabel,
} from "../utils/companyStatuses";
import {
  formatEntityAddedDateText,
  resolveEntityAddedDate,
} from "../utils/entityDateText";
import {
  buildCompanyHiringManagerOptions,
  normalizeHiringManagerContactKind,
} from "../utils/hiringManagers";
import { isRecruiterContact } from "../utils/recruiterOwnership";
import { findTabLabel } from "../utils/breadcrumbs";
import { getEntityLocationValues } from "../utils/locationFilters";

const BACKGROUND_TASK_STATUS_POLL_MS = 1500;
const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");

const normalizeTab = (t) => {
  const v = String(t || "").toLowerCase();
  const ok = ["details", "leads", "contacts", "jobs", "candidates", "activities"];
  return ok.includes(v) ? v : "details";
};

const pickPreferredCompanyContact = (options = []) => {
  const safeOptions = (Array.isArray(options) ? options : []).filter(Boolean);
  const nonRecruiterOptions = safeOptions.filter(
    (option) =>
      normalizeHiringManagerContactKind(option?.contact_kind, "hiring_manager") !== "recruiter"
  );
  const withReachableDetails = nonRecruiterOptions.filter(
    (option) => option?.phone || option?.email || option?.linkedin_url
  );

  return (
    withReachableDetails.find(
      (option) =>
        normalizeHiringManagerContactKind(option?.contact_kind, "hiring_manager") ===
        "hiring_manager"
    ) ||
    withReachableDetails[0] ||
    nonRecruiterOptions.find(
      (option) =>
        normalizeHiringManagerContactKind(option?.contact_kind, "hiring_manager") ===
        "hiring_manager"
    ) ||
    nonRecruiterOptions[0] ||
    null
  );
};

const buildCompanyProposalPrompt = (company = {}, contact = null) => {
  const companyName = String(
    company?.company_name || company?.companyName || company?.name || "the company"
  ).trim();
  const detailParts = [
    companyName,
    String(company?.website || company?.company_website || "").trim(),
    String(company?.location || "").trim(),
    String(company?.status || "").trim(),
    String(company?.industry || company?.industry_sector || "").trim(),
  ].filter(Boolean);
  const contactParts = [
    String(contact?.name || "").trim(),
    String(contact?.role || "").trim(),
    String(contact?.email || "").trim(),
    String(contact?.phone || "").trim(),
  ].filter(Boolean);

  return [
    `Create a concise recruiting proposal for ${companyName}.`,
    "Include likely hiring pain points, an RPO engagement outline, sourcing strategy, candidate profile angles, and a short outreach-ready summary.",
    "",
    `Company: ${detailParts.join(" | ") || companyName}`,
    contactParts.length ? `Primary contact: ${contactParts.join(" | ")}` : "",
  ]
    .filter(Boolean)
    .join("\n");
};

const buildCompanyPitchPackagePrompt = (company = {}, contact = null) => {
  const companyName = String(
    company?.company_name || company?.companyName || company?.name || "the company"
  ).trim();
  const detailParts = [
    companyName,
    String(company?.website || company?.company_website || "").trim(),
    String(company?.location || "").trim(),
    String(company?.industry || company?.industry_sector || "").trim(),
  ].filter(Boolean);
  const contactParts = [
    String(contact?.name || "").trim(),
    String(contact?.role || "").trim(),
    String(contact?.email || "").trim(),
  ].filter(Boolean);

  return [
    `Create a compelling recruiting pitch package for ${companyName}.`,
    "Include an executive summary, hiring challenges, recommended RPO solution, sourcing approach, delivery timeline, success metrics, and a concise outreach-ready pitch.",
    "Format the result so it can be reused in Content -> Pitch Packages.",
    "",
    `Company: ${detailParts.join(" | ") || companyName}`,
    contactParts.length ? `Primary contact: ${contactParts.join(" | ")}` : "",
  ]
    .filter(Boolean)
    .join("\n");
};

const CLIENT_STATUS_OPTIONS = [
  "New",
  "Contacted",
  "Discovery",
  "Proposal",
  "Client",
  "Active",
  "No stage",
  "Do not Contact",
];

const getToken = () => {
  const direct =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("idToken") ||
    localStorage.getItem("id_token") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("jwt") ||
    sessionStorage.getItem("accessToken") ||
    sessionStorage.getItem("access_token") ||
    sessionStorage.getItem("idToken") ||
    sessionStorage.getItem("id_token");

  if (direct && String(direct).trim()) return String(direct).trim();

  const tryJsonKeys = ["user", "auth", "session", "profile"];
  for (const key of tryJsonKeys) {
    try {
      const raw = localStorage.getItem(key) || sessionStorage.getItem(key);
      if (!raw) continue;
      const obj = JSON.parse(raw);
      const token =
        obj?.token ||
        obj?.accessToken ||
        obj?.access_token ||
        obj?.jwt ||
        obj?.idToken ||
        obj?.id_token;
      if (token && String(token).trim()) return String(token).trim();
    } catch {
      // ignore malformed session data
    }
  }

  return "";
};

const getAuthHeaders = () => {
  const token = getToken();

  if (!token) return {};

  const rawToken = token.replace(/^Bearer\s+/i, "");
  return {
    Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
    "x-access-token": rawToken,
  };
};

const buildContactIdentity = (row = {}) => [
  row?.id != null ? `id:${row.id}` : "",
  String(row?.name || "").trim().toLowerCase(),
  String(row?.role || "").trim().toLowerCase(),
  String(row?.contact_kind || "").trim().toLowerCase(),
  String(row?.entity_type || "").trim().toLowerCase(),
  row?.entity_id != null ? String(row.entity_id) : "",
].join("|");

const areCompanyContactsEqual = (left = [], right = []) => {
  if (left === right) return true;
  if (!Array.isArray(left) || !Array.isArray(right)) return false;
  if (left.length !== right.length) return false;

  for (let index = 0; index < left.length; index += 1) {
    if (buildContactIdentity(left[index]) !== buildContactIdentity(right[index])) {
      return false;
    }
  }

  return true;
};

const buildCompanyEntityRowIdentity = (row = {}) => [
  row?.id != null ? `id:${row.id}` : "",
  String(row?.status || "").trim().toLowerCase(),
  String(row?.updated_at || row?.created_at || row?.last_sync || "").trim(),
  String(row?.contact_name || row?.job_title || row?.name || "").trim().toLowerCase(),
].join("|");

const areCompanyEntityRowsEqual = (left = [], right = []) => {
  if (left === right) return true;
  if (!Array.isArray(left) || !Array.isArray(right)) return false;
  if (left.length !== right.length) return false;

  for (let index = 0; index < left.length; index += 1) {
    if (buildCompanyEntityRowIdentity(left[index]) !== buildCompanyEntityRowIdentity(right[index])) {
      return false;
    }
  }

  return true;
};

const getArrayOrEmpty = (value) => (Array.isArray(value) ? value : []);
const uniqTextValues = (values = []) =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );

const normalizeCountableTextArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .filter(Boolean)
      .map((item) => String(item).trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .filter(Boolean)
          .map((item) => String(item).trim())
          .filter(Boolean);
      }
    } catch {
      // Treat non-JSON text as a delimited list.
    }

    return trimmed
      .split(/\s*(?:,|\||\/|&)\s*/g)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const sanitizeCompanyContacts = (value) => {
  const seen = new Set();

  return getArrayOrEmpty(value).filter((row) => {
    if (!row || typeof row !== "object" || isRecruiterContact(row)) return false;

    // Keep the tab count aligned with CompanyContactsTabContent, which renders
    // one card per person (name + source entity) and merges duplicate imports.
    const nameKey = String(row.name || row.contact_name || "").trim().toLowerCase();
    const entityKey = Number(row.entity_id ?? row.entityId) || 0;
    const linkedinKey = String(row.linkedin_url || row.linkedin || "").trim().toLowerCase();
    const emailKey = String(row.email || row.contact_email || "").trim().toLowerCase();
    const key = nameKey ? `${nameKey}::${entityKey}` : linkedinKey || emailKey;

    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const toFiniteCount = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const toPositiveId = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : null;
};

const pickPositiveId = (...values) => {
  for (const value of values) {
    const nextId = toPositiveId(value);
    if (nextId) return nextId;
  }

  return null;
};



const mergeCompanyDetailState = (previousCompany, nextCompany) => {
  if (!nextCompany) return null;
  if (!previousCompany || String(previousCompany?.id || "") !== String(nextCompany?.id || "")) {
    return nextCompany;
  }

  const hasNextContacts = Array.isArray(nextCompany.contacts);
  const hasNextRelatedLeads = Array.isArray(nextCompany.related_leads);
  const hasNextActivities = Array.isArray(nextCompany.activities);
  const hasNextJobs = Array.isArray(nextCompany.jobs);

  const contacts = hasNextContacts
    ? sanitizeCompanyContacts(nextCompany.contacts)
    : sanitizeCompanyContacts(previousCompany.contacts);
  const relatedLeads = hasNextRelatedLeads
    ? getArrayOrEmpty(nextCompany.related_leads)
    : getArrayOrEmpty(previousCompany.related_leads);
  const activities = hasNextActivities
    ? getArrayOrEmpty(nextCompany.activities)
    : getArrayOrEmpty(previousCompany.activities);
  const jobs = hasNextJobs ? getArrayOrEmpty(nextCompany.jobs) : getArrayOrEmpty(previousCompany.jobs);

  const contactsCount = resolveMergedCount({
    nextCount: nextCompany.contactsCount ?? nextCompany.contacts_count,
    previousCount: previousCompany.contactsCount ?? previousCompany.contacts_count,
    fallbackLength: contacts.length,
    hasNextDetail: hasNextContacts,
  });
  const leadsCount = resolveMergedCount({
    nextCount: nextCompany.leadsCount ?? nextCompany.leads_count,
    previousCount: previousCompany.leadsCount ?? previousCompany.leads_count,
    fallbackLength: relatedLeads.length,
    hasNextDetail: hasNextRelatedLeads,
  });
  const jobsCount = resolveMergedCount({
    nextCount:
      nextCompany.jobsCount ?? nextCompany.jobs_count ?? nextCompany.jobOpenings ?? nextCompany.job_openings,
    previousCount:
      previousCompany.jobsCount ??
      previousCompany.jobs_count ??
      previousCompany.jobOpenings ??
      previousCompany.job_openings,
    fallbackLength: jobs.length,
    hasNextDetail: hasNextJobs,
  });
  const candidatesCount = resolveMergedCount({
    nextCount: nextCompany.candidatesCount ?? nextCompany.candidates_count,
    previousCount: previousCompany.candidatesCount ?? previousCompany.candidates_count,
    fallbackLength: 0,
    hasNextDetail: false,
  });
  const activitiesCount = resolveMergedCount({
    nextCount: nextCompany.activitiesCount ?? nextCompany.activities_count,
    previousCount: previousCompany.activitiesCount ?? previousCompany.activities_count,
    fallbackLength: activities.length,
    hasNextDetail: hasNextActivities,
  });

  const previousLocations = getEntityLocationValues(previousCompany);
  const nextLocations = getEntityLocationValues(nextCompany);
  const nextHasSavedLocationList = [
    nextCompany?.locations,
    nextCompany?.locations_json,
    nextCompany?.locationsJson,
    nextCompany?.location_values,
    nextCompany?.raw?.locations,
    nextCompany?.raw?.locations_json,
    nextCompany?.raw?.locationsJson,
    nextCompany?.raw?.location_values,
  ].some((value) => getEntityLocationValues({ locations: value }).length > 0);
  // Partial parent/table updates commonly contain only `location`. Keep the
  // complete persisted collection already loaded for this company until an
  // explicit multi-location payload supersedes it.
  const locations = nextHasSavedLocationList
    ? nextLocations
    : nextLocations.length >= previousLocations.length
    ? nextLocations
    : previousLocations;

  return {
    ...previousCompany,
    ...nextCompany,
    contacts,
    related_leads: relatedLeads,
    activities,
    jobs,
    contactsCount,
    contacts_count: contactsCount,
    leadsCount,
    leads_count: leadsCount,
    jobsCount,
    jobs_count: jobsCount,
    jobOpenings: jobsCount,
    job_openings: jobsCount,
    candidatesCount,
    candidates_count: candidatesCount,
    activitiesCount,
    activities_count: activitiesCount,
    locations,
    locations_json: locations,
    locationsJson: locations,
    location_values: locations,
    location: nextCompany.location || locations[0] || previousCompany.location || "",
  };
};

export default function LeadTabsLayout({
  company,
  entityType: entityTypeProp,
  detailFieldOptions,
  onBack,
  onCompanyChange,
  onTabChange,
  onOpenLeadDetails,
  initialTab = "details",
  backLabel = "Companies",
}) {
  const navigate = useNavigate();
  const location = useLocation();
  // The query string is the public navigation contract for a company profile.
  // Keep it authoritative here as well as in the parent page: a parent refresh
  // can otherwise briefly pass its previous tab prop back down after a tab link
  // has already updated the URL, leaving the Details view on screen.
  const routeTab = useMemo(() => {
    const requestedTab = new URLSearchParams(location.search).get("tab");
    return requestedTab ? normalizeTab(requestedTab) : null;
  }, [location.search]);
  const requestedTab = routeTab || normalizeTab(initialTab);

  const [tab, handleTabChange] = useProfileTab(requestedTab, normalizeTab, onTabChange);
  const [statusValue, setStatusValue] = useState(String(company?.status || "New"));
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusToast, setStatusToast] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const [companyLive, setCompanyLive] = useState(company || null);
  // Mirror companyLive in a ref so async callbacks can read the latest value without
  // needing a functional updater (which would run during React's render phase and cause
  // "Cannot update a component while rendering" warnings if onCompanyChange is called).
  const companyLiveRef = React.useRef(companyLive);
  companyLiveRef.current = companyLive;
  const onCompanyChangeRef = React.useRef(onCompanyChange);
  const [detailLoaded, setDetailLoaded] = useState(false);
  const [detailLoadedEntityId, setDetailLoadedEntityId] = useState("");
  // Background-task polling reuses the detail request. Keep the already-rendered
  // company view marked as loaded for those refreshes; otherwise every poll makes
  // tab badges and dependent content briefly reset, which looks like flickering.
  const detailLoadOwnerKeyRef = React.useRef("");
  // Tracks whether the dedicated jobs-count prefetch has completed for the current company.
  // We only reveal the jobs tab count after this authoritative fetch so that stale
  // denormalized counts passed in via the `company` prop (e.g. from the drawer's
  // job_openings field) never flash in the tab header before the real count arrives.
  const [jobsCountPrefetched, setJobsCountPrefetched] = useState(false);
  // The full Jobs-tab response is retained separately from the company detail object.
  // Parent list refreshes can carry an old jobsCount: 0 and must not change this value.
  const [loadedCompanyJobsCount, setLoadedCompanyJobsCount] = useState(null);
  // A loaded Jobs tab has the complete, company-scoped result set. Keep track of it so
  // the background limit=1 count request cannot replace that real result with a stale zero.
  const jobsTabResultCompanyIdRef = React.useRef("");
  // Incremented whenever the same company is re-navigated to (e.g. via "See More" from a
  // popup on a different tab). The prefetch effect depends on this so it re-runs even when
  // resolvedCompanyEntityId doesn't change, ensuring we never display a stale popup count.
  const [prefetchKey, setPrefetchKey] = useState(0);
  const [leadDialogOpen, setLeadDialogOpen] = useState(false);
  const [leadDialogValue, setLeadDialogValue] = useState(null);
  const [leadDialogInitialScreen, setLeadDialogInitialScreen] = useState("start");
  const [leadDialogMode, setLeadDialogMode] = useState("lead");
  const [leadReloadKey, setLeadReloadKey] = useState(0);
  const [jobsReloadKey, setJobsReloadKey] = useState(0);
  const [contactsReloadKey, setContactsReloadKey] = useState(0);
  const [visibleContactsCount, setVisibleContactsCount] = useState(null);
  const [addJobOpen, setAddJobOpen] = useState(false);
  const [proposalDialogOpen, setProposalDialogOpen] = useState(false);
  const [proposalDialogPrompt, setProposalDialogPrompt] = useState("");
  const [proposalDialogSubmitOnOpen, setProposalDialogSubmitOnOpen] = useState(false);
  const [sequenceDialogOpen, setSequenceDialogOpen] = useState(false);
  const localDetailMutationVersionRef = React.useRef(0);
  // Keep a confirmed logo while a background detail refresh catches up.  A
  // refresh can begin just after the upload request, before a replica/cache
  // serves the saved value, so a request-version check alone is not enough.
  const pendingLogoUrlRef = React.useRef("");
  const rawEntityType = String(
    entityTypeProp || company?.entityType || companyLive?.entityType || ""
  ).toLowerCase();
  const entityLooksLikeCompany =
    rawEntityType === "company" ||
    Boolean(
      company?.company_link ||
        company?.company_website ||
        company?.career_site ||
        company?.career_website ||
        company?.industry_sector ||
        company?.company_size ||
        company?.ownership_type ||
        companyLive?.company_link ||
        companyLive?.company_website ||
        companyLive?.career_site ||
        companyLive?.career_website ||
        companyLive?.industry_sector ||
        companyLive?.company_size ||
        companyLive?.ownership_type
    );
  const entityType = entityLooksLikeCompany
    ? "company"
    : rawEntityType === "contact"
    ? "contact"
    : "lead";
  const isCompanyEntity = entityType === "company";

  useEffect(() => {
    onCompanyChangeRef.current = onCompanyChange;
  }, [onCompanyChange]);

  const emitCompanyChange = useCallback((nextCompany) => {
    onCompanyChangeRef.current?.(nextCompany);
  }, []);

  useEffect(() => {
    setCompanyLive((prev) => {
      if (!company) return null;
      // The table parent frequently recreates this object. Do not let that
      // stale snapshot replace activities that were just saved in this open
      // profile; a detail fetch owns updates for the current company.
      if (
        prev &&
        String(prev?.id || prev?.company_id || "") ===
          String(company?.id || company?.company_id || "")
      ) {
        return prev;
      }
      const nextCompany = isCompanyEntity
        ? {
            ...company,
            status: normalizeCompanyStatusLabel(company?.status, {
              fallback: "New",
              preserveUnknown: true,
            }),
          }
        : company;
      return mergeCompanyDetailState(prev, {
        ...nextCompany,
        entityType,
      });
    });
  // The prop object is intentionally excluded: its table snapshot can be
  // older than the live profile state following a status change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, company?.company_id, entityType, isCompanyEntity]);

  useEffect(() => {
    const nextStatus = isCompanyEntity
      ? normalizeCompanyStatusLabel(companyLive?.status || company?.status, {
          fallback: "New",
          preserveUnknown: true,
        })
      : String(companyLive?.status || company?.status || "New");
    setStatusValue(nextStatus);
  }, [companyLive?.status, company?.status, isCompanyEntity]);

  const dateText = useMemo(
    () => formatEntityAddedDateText(resolveEntityAddedDate(companyLive, company)),
    [companyLive, company]
  );

  const activeCompany = companyLive || company;
  const aiCompanySeed = useMemo(() => {
    const companyName = String(
      activeCompany?.company_name || activeCompany?.name || ""
    ).trim();
    const website = String(
      activeCompany?.company_website || activeCompany?.website || ""
    ).trim();
    const careerSite = String(
      activeCompany?.career_site || activeCompany?.career_website || ""
    ).trim();
    const companyLink = String(
      activeCompany?.company_link || website || careerSite || ""
    ).trim();

    return {
      name: companyName,
      website,
      career_site: careerSite,
      company_link: companyLink,
      created_at: resolveEntityAddedDate(activeCompany),
      updated_at: String(
        activeCompany?.updated_at ||
          activeCompany?.updatedAt ||
          activeCompany?.last_sync ||
          activeCompany?.lastSync ||
          ""
      ).trim(),
      notes: companyName
        ? `Create or refresh ${companyName}, scan the public website for company information, leadership, team members, and current openings.`
        : "",
    };
  }, [activeCompany]);
  const hiringManagerSummaryItems = useMemo(() => {
    const companyName = String(
      activeCompany?.company_name || activeCompany?.companyName || activeCompany?.name || ""
    ).trim();
    const primaryLocation =
      String(activeCompany?.location || "").trim() ||
      normalizeCountableTextArray(
        activeCompany?.locations ||
          activeCompany?.locations_json ||
          activeCompany?.locationsJson ||
          []
      )[0] ||
      "";
    const industryLabel = String(
      activeCompany?.industry_sector ||
        activeCompany?.industry ||
        activeCompany?.company_industry ||
        ""
    ).trim();
    const statusLabel = String(statusValue || "").trim();

    return uniqTextValues([companyName, primaryLocation, industryLabel, statusLabel]);
  }, [activeCompany, statusValue]);
  const resolvedLeadEntityId = toPositiveId(
    activeCompany?.lead_id ??
      activeCompany?.leadId ??
      activeCompany?.entity_id ??
      company?.lead_id ??
      company?.leadId ??
      company?.entity_id ??
      activeCompany?.id ??
      company?.id
  );
  const resolvedCompanyEntityId = isCompanyEntity
    ? pickPositiveId(
        activeCompany?.id,
        company?.id,
        activeCompany?.company_id,
        activeCompany?.companyId,
        activeCompany?.company?.id,
        company?.company_id,
        company?.companyId,
        company?.company?.id
      )
    : pickPositiveId(
        activeCompany?.company_id,
        activeCompany?.companyId,
        activeCompany?.company?.id,
        company?.company_id,
        company?.companyId,
        company?.company?.id
      );
  const detailEntityId = isCompanyEntity ? resolvedCompanyEntityId : resolvedLeadEntityId;
  const routeCompanyEntityId = useMemo(() => {
    const rawValue = new URLSearchParams(location.search).get("companyId");
    return toPositiveId(rawValue);
  }, [location.search]);
  const jobsCompanyEntityId = isCompanyEntity
    ? routeCompanyEntityId || resolvedCompanyEntityId
    : resolvedCompanyEntityId;

  useEffect(() => {
    setVisibleContactsCount(null);
  }, [resolvedCompanyEntityId]);

  useEffect(() => {
    jobsTabResultCompanyIdRef.current = "";
    setLoadedCompanyJobsCount(null);
    setJobsCountPrefetched(false);
  }, [jobsCompanyEntityId]);

  useEffect(() => {
    if (!detailEntityId) {
      detailLoadOwnerKeyRef.current = "";
      return undefined;
    }

    const controller = new AbortController();
    let active = true;
    const mutationVersionAtRequestStart = localDetailMutationVersionRef.current;
    const detailOwnerKey = `${entityType}:${String(detailEntityId)}`;
    if (detailLoadOwnerKeyRef.current !== detailOwnerKey) {
      detailLoadOwnerKeyRef.current = detailOwnerKey;
      setDetailLoaded(false);
      setDetailLoadedEntityId("");
    }

    const detailUrl = isCompanyEntity
      ? `${API_BASE}/api/companies/${encodeURIComponent(detailEntityId)}`
      : `${API_BASE}/api/leads/${encodeURIComponent(detailEntityId)}`;

    fetch(detailUrl, {
      headers: {
        ...getAuthHeaders(),
      },
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          const message = await response.text().catch(() => "");
          throw new Error(
            message || `Failed to fetch ${isCompanyEntity ? "company" : "lead"} ${detailEntityId}`
          );
        }
        return response.json();
      })
      .then((payload) => {
        if (!active) return;
        if (localDetailMutationVersionRef.current !== mutationVersionAtRequestStart) {
          setDetailLoaded(true);
          setDetailLoadedEntityId(String(detailEntityId));
          return;
        }
        const nextLead = payload?.company || payload?.lead || payload || null;
        if (nextLead && typeof nextLead === "object") {
          const confirmedLogo = pendingLogoUrlRef.current;
          const responseLogo = String(
            nextLead.company_logo || nextLead.logo || ""
          ).trim();
          const detailWithConfirmedLogo = confirmedLogo && responseLogo !== confirmedLogo
            ? { ...nextLead, logo: confirmedLogo, company_logo: confirmedLogo }
            : nextLead;

          if (confirmedLogo && responseLogo === confirmedLogo) {
            pendingLogoUrlRef.current = "";
          }
          const mergedCompany = mergeCompanyDetailState(companyLiveRef.current, {
            ...detailWithConfirmedLogo,
            entityType,
            contact_repair_pending: Boolean(payload?.contact_repair_pending),
            company_profile_enrichment_pending: Boolean(
              payload?.company_profile_enrichment_pending
            ),
          });
          setCompanyLive(mergedCompany);
          emitCompanyChange(mergedCompany);
        }
        setDetailLoaded(true);
        setDetailLoadedEntityId(String(detailEntityId));
      })
      .catch((err) => {
        if (!active || err?.name === "AbortError") return;
        console.error("Failed to refresh company detail activities:", err);
        setDetailLoaded(true);
        setDetailLoadedEntityId(String(detailEntityId));
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [detailEntityId, leadReloadKey, isCompanyEntity, entityType, emitCompanyChange]);

  useEffect(() => {
    if (!isCompanyEntity || !detailEntityId) return undefined;
    if (
      !activeCompany?.contact_repair_pending &&
      !activeCompany?.company_profile_enrichment_pending
    ) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setLeadReloadKey((prev) => prev + 1);
    }, BACKGROUND_TASK_STATUS_POLL_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    activeCompany?.company_profile_enrichment_pending,
    activeCompany?.contact_repair_pending,
    detailEntityId,
    isCompanyEntity,
  ]);

  useEffect(() => {
    if (!isCompanyEntity || !activeCompany) return;
    syncCompanyBackgroundWork(activeCompany);
  }, [
    activeCompany,
    activeCompany?.company_profile_enrichment_pending,
    activeCompany?.contact_repair_pending,
    isCompanyEntity,
  ]);

  useEffect(() => subscribeCompanyRelationshipsChanged(() => {
    setLeadReloadKey((value) => value + 1);
    setContactsReloadKey((value) => value + 1);
    setJobsReloadKey((value) => value + 1);
  }), []);

  const contactRepairWasPendingRef = React.useRef(false);
  useEffect(() => {
    if (!isCompanyEntity || !resolvedCompanyEntityId || typeof window === "undefined") {
      return undefined;
    }

    const handleCompanyContactCreated = (event) => {
      const createdForCompanyId = Number(event?.detail?.companyId || 0);
      if (createdForCompanyId !== Number(resolvedCompanyEntityId)) return;

      // The global header dialog lives outside this view. Refresh the mounted
      // Contacts tab and the company detail count as soon as it saves.
      setContactsReloadKey((prev) => prev + 1);
      setLeadReloadKey((prev) => prev + 1);
    };

    window.addEventListener("company-contact-created", handleCompanyContactCreated);
    return () => window.removeEventListener("company-contact-created", handleCompanyContactCreated);
  }, [isCompanyEntity, resolvedCompanyEntityId]);

  useEffect(() => {
    if (!isCompanyEntity || !resolvedCompanyEntityId || typeof window === "undefined") {
      return undefined;
    }

    const refreshApprovedReviewRecords = (event) => {
      const updatedCompanyId = Number(event?.detail?.companyId || 0);
      if (updatedCompanyId !== Number(resolvedCompanyEntityId)) return;

      setContactsReloadKey((prev) => prev + 1);
      setLeadReloadKey((prev) => prev + 1);
      setJobsReloadKey((prev) => prev + 1);
      setJobsCountPrefetched(false);
    };

    try {
      const stored = JSON.parse(
        sessionStorage.getItem("rpo:company-review-records-updated") || "null"
      );
      if (Number(stored?.companyId || 0) === Number(resolvedCompanyEntityId)) {
        refreshApprovedReviewRecords({ detail: stored });
        sessionStorage.removeItem("rpo:company-review-records-updated");
      }
    } catch {
      // Ignore an unavailable or malformed session-storage refresh marker.
    }

    window.addEventListener("rpo:company-review-records-updated", refreshApprovedReviewRecords);
    return () => {
      window.removeEventListener("rpo:company-review-records-updated", refreshApprovedReviewRecords);
    };
  }, [isCompanyEntity, resolvedCompanyEntityId]);

  useEffect(() => {
    if (!isCompanyEntity || !resolvedCompanyEntityId || typeof window === "undefined") {
      return undefined;
    }

    const handleJobCreated = (event) => {
      const createdForCompanyId = Number(event?.detail?.companyId || 0);
      if (createdForCompanyId !== Number(resolvedCompanyEntityId)) return;

      // A job can be created while this profile is mounted. Reload the same
      // company-scoped query used by the Jobs tab so its rows and badge update.
      setJobsReloadKey((prev) => prev + 1);
      setLeadReloadKey((prev) => prev + 1);
      setJobsCountPrefetched(false);
    };

    window.addEventListener("job-created", handleJobCreated);
    return () => window.removeEventListener("job-created", handleJobCreated);
  }, [isCompanyEntity, resolvedCompanyEntityId]);

  useEffect(() => {
    if (!isCompanyEntity || !detailEntityId) {
      contactRepairWasPendingRef.current = false;
      return;
    }

    const isContactRepairPending = Boolean(activeCompany?.contact_repair_pending);
    if (contactRepairWasPendingRef.current && !isContactRepairPending) {
      setContactsReloadKey((prev) => prev + 1);
    }
    contactRepairWasPendingRef.current = isContactRepairPending;
  }, [activeCompany?.contact_repair_pending, detailEntityId, isCompanyEntity]);

  useEffect(() => {
    if (!isCompanyEntity || !jobsCompanyEntityId) return undefined;

    const controller = new AbortController();
    let active = true;

    fetch(
      // Use the same company-scoped request as the Jobs tab. Some older responses
      // report total: 0 even though they include matching rows, so a limit=1 count
      // request cannot reliably represent the company profile's Jobs count.
      `${API_BASE}/api/jobs?page=1&limit=100&sort=created_at&order=desc&companyId=${encodeURIComponent(
        String(jobsCompanyEntityId)
      )}`,
      {
        headers: {
          ...getAuthHeaders(),
        },
        credentials: "include",
        signal: controller.signal,
      }
    )
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.error || `Failed to fetch company jobs (${response.status})`);
        }
        return payload;
      })
      .then((payload) => {
        if (!active) return;

        // Once the Jobs tab has loaded this company, its rows/total are the source of
        // truth. Some deployments return a stale zero from the small prefetch query
        // even while the full table query has rows, which previously reset the badge.
        if (jobsTabResultCompanyIdRef.current === String(jobsCompanyEntityId)) {
          setJobsCountPrefetched(true);
          return;
        }

        const returnedRows = getArrayOrEmpty(payload?.rows);
        const reportedJobsCount = toFiniteCount(
          payload?.total ?? payload?.count ?? payload?.jobsCount
        );
        // Never let an incorrect total: 0 hide actual rows returned for this company.
        const nextJobsCount = Math.max(returnedRows.length, reportedJobsCount ?? 0);

        // Read current state from the ref (not a functional updater) so that
        // onCompanyChange can be called outside of any React setState, avoiding the
        // "Cannot update a component while rendering a different component" warning
        // that React 18 concurrent mode raises when parent setState is called from
        // inside a child's functional updater.
        const prev = companyLiveRef.current;
        if (prev && String(prev?.entityType || "").toLowerCase() === "company") {
          const currentJobsCount =
            toFiniteCount(
              prev.jobsCount ?? prev.jobs_count ?? prev.jobOpenings ?? prev.job_openings
            ) ?? 0;
          const staleJobsArrayLength = getArrayOrEmpty(prev.jobs).length;
          // A company-detail response can already have the correct relationship count.
          // Do not replace a positive, loaded count with a broken total: 0 response.
          const resolvedJobsCount =
            nextJobsCount === 0 && currentJobsCount > 0 ? currentJobsCount : nextJobsCount;
          // Skip update only when both the count and the array are already consistent.
          // If the stale jobs array (e.g. fake placeholder items from buildJobs) is longer
          // than the authoritative count we must still clear it to prevent the tab badge
          // from being inflated by Math.max(count, jobs.length).
          if (currentJobsCount !== resolvedJobsCount || staleJobsArrayLength > resolvedJobsCount) {
            const nextCompany = mergeCompanyDetailState(prev, {
              ...prev,
              jobsCount: resolvedJobsCount,
              jobs_count: resolvedJobsCount,
              jobOpenings: resolvedJobsCount,
              job_openings: resolvedJobsCount,
              // When the authoritative count is lower than the stale array length, clear
              // the array so placeholder items (e.g. "1 open role" from buildJobs) cannot
              // inflate the displayed count via the Math.max(count, array.length) formula.
              ...(staleJobsArrayLength > resolvedJobsCount ? { jobs: [] } : {}),
            });
            setCompanyLive(nextCompany);
            emitCompanyChange(nextCompany);
          }
        }
        // Mark the authoritative prefetch as complete so the tab count is revealed.
        setJobsCountPrefetched(true);
      })
      .catch((err) => {
        if (!active || err?.name === "AbortError") return;
        console.error("Failed to prefetch company jobs count:", err);
        // Even on error, reveal the count so the tab doesn't stay blank indefinitely.
        setJobsCountPrefetched(true);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [isCompanyEntity, emitCompanyChange, jobsCompanyEntityId, prefetchKey, jobsReloadKey]);

  const hasLeadsCountEvidence = Boolean(
    getArrayOrEmpty(activeCompany?.related_leads).length ||
      toFiniteCount(activeCompany?.leadsCount ?? activeCompany?.leads_count)
  );
  const hasActivitiesCountEvidence = Boolean(
    getArrayOrEmpty(activeCompany?.activities).length ||
      toFiniteCount(activeCompany?.activitiesCount ?? activeCompany?.activities_count)
  );
  const detailLoadedForCurrentEntity =
    detailLoaded && String(detailLoadedEntityId) === String(detailEntityId || "");
  const shouldHidePendingLeadCount =
    isCompanyEntity && Boolean(detailEntityId) && !detailLoadedForCurrentEntity && !hasLeadsCountEvidence;
  // Wait for the authoritative company detail before displaying the Contacts count.
  // This prevents the initial create/list value from flashing before the hydrated total.
  const shouldHidePendingContactsCount =
    isCompanyEntity && Boolean(detailEntityId) && !detailLoadedForCurrentEntity;
  // For company entities the jobs count is managed exclusively by the dedicated prefetch
  // (limit=1 query against the jobs table). We hide it until that prefetch has resolved
  // so that stale denormalized values from the initial `company` prop (e.g. a cached
  // job_openings field passed through from the popup drawer) never appear in the tab header.
  const shouldHidePendingJobsCount =
    isCompanyEntity && Boolean(detailEntityId) && !jobsCountPrefetched;
  const shouldHidePendingActivitiesCount =
    isCompanyEntity && Boolean(detailEntityId) && !detailLoadedForCurrentEntity && !hasActivitiesCountEvidence;
  const breadcrumbTabs = useMemo(
    () => [
      { value: "details", label: "Details" },
      { value: "leads", label: "Leads" },
      { value: "contacts", label: "Contacts" },
      { value: "jobs", label: "Jobs" },
      { value: "candidates", label: "Candidates" },
      { value: "activities", label: "Activities" },
    ],
    []
  );
  const companyHeaderTabs = useMemo(
    () =>
      isCompanyEntity
        ? [
            { value: "details", label: "Details" },
            { value: "leads", label: "Leads", countKey: "leads" },
            { value: "contacts", label: "Contacts", countKey: "contacts" },
            { value: "jobs", label: "Jobs", countKey: "jobs" },
            { value: "activities", label: "Activities", countKey: "activities" },
          ]
        : undefined,
    [isCompanyEntity]
  );
  const activeBreadcrumbLabel = useMemo(
    () => findTabLabel(breadcrumbTabs, tab),
    [breadcrumbTabs, tab]
  );

  const counts = {
    leads: shouldHidePendingLeadCount
      ? undefined
      : toFiniteCount(activeCompany?.leadsCount ?? activeCompany?.leads_count) ??
        getArrayOrEmpty(activeCompany?.related_leads).length,
    contacts: shouldHidePendingContactsCount
      ? undefined
      : visibleContactsCount ??
        (Array.isArray(activeCompany?.contacts)
          ? sanitizeCompanyContacts(activeCompany.contacts).length
          : toFiniteCount(activeCompany?.contactsCount ?? activeCompany?.contacts_count) ?? 0),
    jobs: shouldHidePendingJobsCount
      ? undefined
      : (jobsTabResultCompanyIdRef.current === String(jobsCompanyEntityId)
          ? toFiniteCount(loadedCompanyJobsCount)
          : null) ??
        toFiniteCount(
          activeCompany?.jobsCount ??
            activeCompany?.jobs_count ??
            activeCompany?.jobOpenings ??
            activeCompany?.job_openings
        ) ?? getArrayOrEmpty(activeCompany?.jobs).length,
    candidates: Math.max(
      toFiniteCount(activeCompany?.candidatesCount ?? activeCompany?.candidates_count) ?? 0,
      getArrayOrEmpty(activeCompany?.candidates).length
    ),
    activities: shouldHidePendingActivitiesCount
      ? undefined
      : Math.max(
      toFiniteCount(activeCompany?.activitiesCount ?? activeCompany?.activities_count) ?? 0,
      getArrayOrEmpty(activeCompany?.activities).length
    ),
  };

  const teamCount = Math.max(
    toFiniteCount(
      activeCompany?.teamMembersCount ??
        activeCompany?.team_members_count ??
        activeCompany?.ownersCount ??
        activeCompany?.owners_count
    ) ?? 0,
    normalizeCountableTextArray(
      activeCompany?.team_members ??
        activeCompany?.team_member ??
        activeCompany?.owner_names ??
        activeCompany?.owner_name ??
        activeCompany?.owners ??
        activeCompany?.owner
    ).length
  );

    const proposalCount = getArrayOrEmpty(activeCompany?.related_leads).filter((row) => {
      const rawStatus = String(row?.status || row?.lead_status || "").trim().toLowerCase();
      return rawStatus === "proposal";
    }).length;

    const stats = isCompanyEntity
      ? [
          {
            id: "leads",
            value: counts.leads != null ? String(counts.leads) : "-",
            label: "N° of Leads",
          },
          {
            id: "contacts",
            value: counts.contacts != null ? String(counts.contacts) : "-",
            label: "Contacts",
          },
          { id: "proposals", value: String(proposalCount), label: "Proposals" },
          {
            id: "jobs",
            value: counts.jobs != null ? String(counts.jobs) : "-",
            label: "Jobs",
          },
          {
            id: "candidates",
            value: counts.candidates != null ? String(counts.candidates) : "-",
            label: "Candidates",
          },
          { id: "team", value: String(teamCount), label: "Hiring Team" },
        ]
      : [
          {
            id: "leads",
            value: counts.leads != null ? String(counts.leads) : "-",
            label: "Leads",
          },
          {
            id: "contacts",
            value: counts.contacts != null ? String(counts.contacts) : "-",
            label: "Contacts",
          },
          { id: "jobs", value: counts.jobs != null ? String(counts.jobs) : "-", label: "Jobs" },
          {
            id: "candidates",
            value: counts.candidates != null ? String(counts.candidates) : "-",
            label: "Candidates",
          },
          {
            id: "activities",
            value: counts.activities != null ? String(counts.activities) : "-",
            label: "Activities",
          },
          { id: "team", value: String(teamCount), label: "Team" },
        ];

  const companyContactOptions = useMemo(
    () => buildCompanyHiringManagerOptions(activeCompany),
    [activeCompany]
  );
  const preferredCompanyContact = useMemo(
    () => pickPreferredCompanyContact(companyContactOptions),
    [companyContactOptions]
  );
  const preferredCompanyPhone = useMemo(
    () =>
      String(
        preferredCompanyContact?.phone ||
          activeCompany?.contact_phone ||
          activeCompany?.phone ||
          activeCompany?.job_primary_contact_phone ||
          activeCompany?.company_primary_contact_phone ||
          ""
      ).trim(),
    [activeCompany, preferredCompanyContact]
  );
  const preferredCompanyEmail = useMemo(
    () =>
      String(
        preferredCompanyContact?.email ||
          activeCompany?.contact_email ||
          activeCompany?.email ||
          activeCompany?.job_primary_contact_email ||
          activeCompany?.company_primary_contact_email ||
          ""
      ).trim(),
    [activeCompany, preferredCompanyContact]
  );
  const addJobInitialValues = useMemo(
    () => ({
      company: {
        ...(activeCompany && typeof activeCompany === "object" ? activeCompany : {}),
        id: resolvedCompanyEntityId || activeCompany?.id || null,
        company_id:
          resolvedCompanyEntityId ||
          activeCompany?.company_id ||
          activeCompany?.companyId ||
          activeCompany?.id ||
          null,
      },
      hiringManager: preferredCompanyContact,
      source: String(activeCompany?.source || "").trim(),
      location: String(activeCompany?.location || "").trim(),
      company_website: String(
        activeCompany?.company_website || activeCompany?.website || ""
      ).trim(),
      company_career_website: String(
        activeCompany?.company_career_website ||
          activeCompany?.career_website ||
          activeCompany?.career_site ||
          ""
      ).trim(),
      company_industry: String(
        activeCompany?.company_industry ||
          activeCompany?.industry ||
          activeCompany?.industry_sector ||
          ""
      ).trim(),
      full_company_description: String(
        activeCompany?.full_company_description || ""
      ).trim(),
    }),
    [activeCompany, preferredCompanyContact, resolvedCompanyEntityId]
  );
  const handleQuickActionCall = useCallback(() => {
    openContactCall({ ...preferredCompanyContact, phone: preferredCompanyPhone, company_name: activeCompany?.name });
  }, [preferredCompanyPhone, preferredCompanyContact, activeCompany?.name]);
  const handleQuickActionEmail = useCallback(() => {
    openContactEmail({ ...preferredCompanyContact, email: preferredCompanyEmail, company_name: activeCompany?.name });
  }, [preferredCompanyEmail, preferredCompanyContact, activeCompany?.name]);
  const handleQuickActionProposal = useCallback(() => {
    setProposalDialogPrompt(
      buildCompanyProposalPrompt(activeCompany, preferredCompanyContact)
    );
    setProposalDialogSubmitOnOpen(true);
    setProposalDialogOpen(true);
  }, [activeCompany, preferredCompanyContact]);
  const handleQuickActionPitchPackage = useCallback(() => {
    setProposalDialogPrompt(
      buildCompanyPitchPackagePrompt(activeCompany, preferredCompanyContact)
    );
    setProposalDialogSubmitOnOpen(true);
    setProposalDialogOpen(true);
  }, [activeCompany, preferredCompanyContact]);
  const handleQuickActionCreateSequence = useCallback(() => {
    setSequenceDialogOpen(true);
  }, []);
  const handleQuickActionAddJob = useCallback(() => {
    setAddJobOpen(true);
  }, []);
  const handleQuickActionAddLead = useCallback(() => {
    const currentCompany = companyLive || company;
    if (!currentCompany) return;
    setLeadDialogMode("lead");
    setLeadDialogValue({ companyName: currentCompany.company_name || currentCompany.name || "", companyId: resolvedCompanyEntityId, source: "LinkedIn" });
    setLeadDialogInitialScreen("overview");
    setLeadDialogOpen(true);
  }, [company, companyLive, resolvedCompanyEntityId]);
  const handleQuickActionAddContact = useCallback(() => {
    const currentCompany = companyLive || company;
    if (!currentCompany || !resolvedCompanyEntityId) return;
    setLeadDialogMode("contact");
    setLeadDialogValue({ companyName: currentCompany.company_name || currentCompany.name || "", companyId: resolvedCompanyEntityId, source: "Manual entry" });
    setLeadDialogInitialScreen("overview");
    setLeadDialogOpen(true);
  }, [company, companyLive, resolvedCompanyEntityId]);
  const refreshQuickActionData = useCallback(() => {
    // Quick-action dialogs mutate one or more related collections. Refresh the
    // mounted profile data in place so a successful save is immediately visible
    // without requiring the browser to reload.
    setLeadReloadKey((prev) => prev + 1);
    setContactsReloadKey((prev) => prev + 1);
    setJobsReloadKey((prev) => prev + 1);
    setJobsCountPrefetched(false);
    setPrefetchKey((prev) => prev + 1);
  }, []);

  const handleQuickActionProposalSubmitted = useCallback(() => {
    refreshQuickActionData();
  }, [refreshQuickActionData]);

  const handleQuickActionJobCreated = useCallback(() => {
    // Keep the profile in its job context as soon as the quick action succeeds.
    handleTabChange("jobs");
    refreshQuickActionData();
  }, [handleTabChange, refreshQuickActionData]);

  const handleStatItemClick = useCallback(
    (item) => {
      const tabByStatId = {
        leads: "leads",
        contacts: "contacts",
        jobs: "jobs",
        candidates: "candidates",
        activities: "activities",
      };
      const nextTab = tabByStatId[String(item?.id || "")];
      if (nextTab) handleTabChange(nextTab);
    },
    [handleTabChange]
  );

  // Declared before the memo so its live callback can be included in the
  // action dependencies even though the callback implementation follows it.
  let handleStatusChange;

  const companyQuickActionItems = useMemo(
    () => [
      {
        key: "call",
        label: "Call",
        onClick: handleQuickActionCall,
        icon: <Call size="18" color="#fff" />,

      },
      {
        key: "email",
        label: "Email",
        onClick: handleQuickActionEmail,
        icon: <Sms size="18" color="#fff" />,
      },
      {
        key: "proposal",
        label: "Proposal",
        onClick: handleQuickActionProposal,
        icon: <ProposalIcon />,
      },
      {
        key: "add_job",
        label: "Add Job",
        onClick: handleQuickActionAddJob,
        icon: <Briefcase size="18" color="#fff" />,
      },
      {
        key: "add_contact",
        label: "Add Contact",
        onClick: handleQuickActionAddContact,
        icon: <Briefcase size="18" color="#fff" />,
      },
      {
        key: "add_lead",
        label: "Add Lead",
        onClick: handleQuickActionAddLead,
        icon: <LeaderboardOutlinedIcon sx={{ fontSize: 18, color: "#fff" }} />,
      },
      {
        key: "convert_to_client",
        label: "Convert to Client",
        onClick: () => Promise.resolve().then(() => handleStatusChange("Active Client")),
        icon: <ProposalIcon />,
      },
      {
        key: "create_sequence",
        label: "Create Sequence",
        onClick: handleQuickActionCreateSequence,
        icon: <LeaderboardOutlinedIcon sx={{ fontSize: 18, color: "#fff" }} />,
      },
      {
        key: "pitch_package",
        label: "Pitch Package",
        onClick: handleQuickActionPitchPackage,
        icon: <ProposalIcon />,
      },
    ],
    [
      handleQuickActionAddContact,
      handleQuickActionAddLead,
      handleQuickActionAddJob,
      handleQuickActionCall,
      handleQuickActionCreateSequence,
      handleQuickActionEmail,
      handleQuickActionPitchPackage,
      handleQuickActionProposal,
      handleStatusChange,
    ]
  );

  const openLeadDialog = useCallback(
    (initialScreen = "overview") => {
      const currentCompany = companyLive || company;
      if (!currentCompany) return;

      setLeadDialogValue({
        companyName:
          currentCompany?.company_name ||
          currentCompany?.name ||
          "",
        website: currentCompany?.website || "",
        location: currentCompany?.location || "",
        source: currentCompany?.source || "LinkedIn",
        leadStatus: currentCompany?.status || "New",
      });
      setLeadDialogInitialScreen(initialScreen);
      setLeadDialogOpen(true);
    },
    [companyLive, company]
  );

  const handleAddNewLead = useCallback(() => {
    openLeadDialog("overview");
  }, [openLeadDialog]);

  const handleSearchNewLead = useCallback(() => {
    openLeadDialog("link");
  }, [openLeadDialog]);

  const handleCompanyLeadsChange = useCallback((nextLeads) => {
    const safeLeads = getArrayOrEmpty(nextLeads).filter((row) => row && typeof row === "object");

    setCompanyLive((prev) => {
      if (!prev) return prev;
      const previousLeads = getArrayOrEmpty(prev.related_leads);
      if (
        areCompanyEntityRowsEqual(previousLeads, safeLeads) &&
        Number(prev.leadsCount ?? prev.leads_count ?? 0) === safeLeads.length
      ) {
        return prev;
      }
      const nextCompany = {
        ...prev,
        related_leads: safeLeads,
        leadsCount: safeLeads.length,
        leads_count: safeLeads.length,
      };
      localDetailMutationVersionRef.current += 1;
      emitCompanyChange(nextCompany);
      return nextCompany;
    });
  }, [emitCompanyChange]);

  const handleCompanyContactsChange = useCallback((nextContacts) => {
    const safeContacts = sanitizeCompanyContacts(nextContacts);

    setCompanyLive((prev) => {
      if (!prev) return prev;
      if (
        areCompanyContactsEqual(prev.contacts || [], safeContacts) &&
        Number(prev.contactsCount ?? prev.contacts_count ?? 0) === safeContacts.length
      ) {
        return prev;
      }
      const nextCompany = {
        ...prev,
        contacts: safeContacts,
        contactsCount: safeContacts.length,
        contacts_count: safeContacts.length,
      };
      localDetailMutationVersionRef.current += 1;
      emitCompanyChange(nextCompany);
      return nextCompany;
    });
  }, [emitCompanyChange]);

  const handleCompanyLogoUpdated = useCallback((logoUrl) => {
    const logo = String(logoUrl || "").trim();
    if (!logo) return;

    // Keep the upload entirely local to the open profile.  Parent list/profile
    // synchronization can remount this view while the image is settling (the
    // full white-page reload shown after an upload), even when the API save
    // succeeded. A later detail load reads the persisted logo normally.
    const previousCompany = companyLiveRef.current;
    if (
      !previousCompany ||
      (previousCompany.logo === logo && previousCompany.company_logo === logo)
    ) {
      return;
    }

    const nextCompany = { ...previousCompany, logo, company_logo: logo };
    // A company-detail request can still be in flight when the upload finishes.
    // Mark this as a local mutation before updating state so that request cannot
    // subsequently replace the just-uploaded logo with its stale response.
    localDetailMutationVersionRef.current += 1;
    pendingLogoUrlRef.current = logo;
    companyLiveRef.current = nextCompany;
    setCompanyLive(nextCompany);
  }, []);

  const handleCompanyJobsChange = useCallback((nextJobsValue) => {
    const safeJobs = getArrayOrEmpty(
      Array.isArray(nextJobsValue) ? nextJobsValue : nextJobsValue?.rows
    ).filter((row) => row && typeof row === "object");
    const explicitJobsCount = Math.max(
      safeJobs.length,
      toFiniteCount(nextJobsValue?.total ?? nextJobsValue?.count ?? nextJobsValue?.jobsCount) ?? 0
    );
    const reportedCompanyId = toPositiveId(nextJobsValue?.companyId);
    const resultCompanyId = reportedCompanyId || jobsCompanyEntityId;
    if (resultCompanyId) {
      jobsTabResultCompanyIdRef.current = String(resultCompanyId);
    }
    if (!jobsCompanyEntityId || String(resultCompanyId) === String(jobsCompanyEntityId)) {
      setLoadedCompanyJobsCount(explicitJobsCount);
    }

    const prev = companyLiveRef.current;
    if (!prev) {
      setJobsCountPrefetched(true);
      return;
    }
    const previousJobs = getArrayOrEmpty(prev.jobs);
    const previousJobsCount =
      Number(prev.jobsCount ?? prev.jobs_count ?? prev.jobOpenings ?? prev.job_openings ?? 0) || 0;

    if (
      areCompanyEntityRowsEqual(previousJobs, safeJobs) &&
      previousJobsCount === explicitJobsCount
    ) {
      setJobsCountPrefetched(true);
      return;
    }

    const nextCompany = {
      ...prev,
      jobs: safeJobs,
      jobsCount: explicitJobsCount,
      jobs_count: explicitJobsCount,
      jobOpenings: explicitJobsCount,
      job_openings: explicitJobsCount,
    };
    localDetailMutationVersionRef.current += 1;
    companyLiveRef.current = nextCompany;
    setCompanyLive(nextCompany);
    emitCompanyChange(nextCompany);
    setJobsCountPrefetched(true);

    if (isCompanyEntity) {
      setLeadReloadKey((prev) => prev + 1);
    }
  }, [isCompanyEntity, emitCompanyChange, jobsCompanyEntityId]);

  const handleCompanyDetailsChange = useCallback(
    (nextCompany) => {
      if (!nextCompany || typeof nextCompany !== "object") return;

      const currentCompany = companyLiveRef.current || company;
      const normalizedNextCompany = {
        ...nextCompany,
        entityType: String(nextCompany.entityType || entityType || "lead").toLowerCase(),
      };
      // Detail sections can finish an older fetch or auto-save after the header
      // status control has saved. Those callbacks do not own the pipeline
      // status, so they must not put the header back to their stale value.
      if (isCompanyEntity && currentCompany?.status) {
        normalizedNextCompany.status = normalizeCompanyStatusLabel(currentCompany.status, {
          fallback: "New",
          preserveUnknown: true,
        });
      }
      const mergedCompany = mergeCompanyDetailState(currentCompany, normalizedNextCompany);

      localDetailMutationVersionRef.current += 1;
      companyLiveRef.current = mergedCompany;
      setCompanyLive(mergedCompany);
      emitCompanyChange(mergedCompany);
    },
    [company, entityType, emitCompanyChange, isCompanyEntity]
  );

  const handleActivitiesChanged = useCallback(() => {
    setLeadReloadKey((prev) => prev + 1);
  }, []);

  handleStatusChange = useCallback(
    async (nextStatus) => {
      const entityId = isCompanyEntity
        ? resolvedCompanyEntityId
        : toPositiveId(activeCompany?.id);
      const normalizedStatus = String(nextStatus || "").trim();
      const previousStatus = isCompanyEntity
        ? normalizeCompanyStatusLabel(activeCompany?.status || statusValue, {
            fallback: "New",
            preserveUnknown: true,
          })
        : String(activeCompany?.status || statusValue || "New");

      if (!entityId || !normalizedStatus || normalizedStatus === previousStatus || statusSaving) {
        setStatusValue(previousStatus);
        return;
      }

      setStatusValue(normalizedStatus);
      setStatusSaving(true);

      try {
        const response = await fetch(
          isCompanyEntity
            ? `${API_BASE}/api/companies/${encodeURIComponent(entityId)}/status`
            : `${API_BASE}/api/leads/bulk-status`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              ...getAuthHeaders(),
            },
            credentials: "include",
            body: JSON.stringify(
              isCompanyEntity
                ? { status: normalizedStatus }
                : {
                    ids: [entityId],
                    status: normalizedStatus,
                  }
            ),
          }
        );

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.error || `Failed to update ${isCompanyEntity ? "company" : "client"} status.`);
        }

        if (isCompanyEntity) {
          const nextCompanyPayload =
            payload?.company && typeof payload.company === "object"
              ? payload.company
              : { status: normalizedStatus };

          const nextCompany = {
            ...(activeCompany || {}),
            ...nextCompanyPayload,
            entityType: "company",
          };

          localDetailMutationVersionRef.current += 1;
          companyLiveRef.current = nextCompany;
          setCompanyLive(nextCompany);
          setStatusValue(
            normalizeCompanyStatusLabel(nextCompany.status || normalizedStatus, {
              fallback: normalizedStatus,
              preserveUnknown: true,
            })
          );
          emitCompanyChange(nextCompany);
          setStatusToast({
            open: true,
            severity: "success",
            message: `Company status updated to "${normalizeCompanyStatusLabel(
              nextCompany.status || normalizedStatus,
              {
                fallback: normalizedStatus,
                preserveUnknown: true,
              }
            )}".`,
          });
          // The PATCH response is the authoritative, just-saved company record.
          // Do not immediately replace it with a separate detail request: that
          // request can have been started before the save and briefly puts the
          // profile (and the Companies table) back on its old status.
          // `emitCompanyChange` above updates the open record and table in place.
          return;
        }

        setCompanyLive((prev) => {
          if (!prev) return prev;
          const nextCompany = { ...prev, status: normalizedStatus };
          localDetailMutationVersionRef.current += 1;
          emitCompanyChange(nextCompany);
          return nextCompany;
        });
        setStatusToast({
          open: true,
          severity: "success",
          message: `Client status updated to "${normalizedStatus}".`,
        });
        setLeadReloadKey((prev) => prev + 1);
      } catch (error) {
        console.error(`Failed to update ${isCompanyEntity ? "company" : "client"} status:`, error);
        setStatusValue(previousStatus);
        if (!isCompanyEntity) {
          setCompanyLive((prev) => {
            if (!prev) return prev;
            const nextCompany = { ...prev, status: previousStatus };
            localDetailMutationVersionRef.current += 1;
            emitCompanyChange(nextCompany);
            return nextCompany;
          });
        }
        setStatusToast({
          open: true,
          severity: "error",
          message: error?.message || `Failed to update ${isCompanyEntity ? "company" : "client"} status.`,
        });
      } finally {
        setStatusSaving(false);
      }
    },
    [activeCompany, emitCompanyChange, resolvedCompanyEntityId, statusSaving, statusValue, isCompanyEntity]
  );

  if (!activeCompany) return null;

  return (
    <>
      <ListTableTabsTemplate
        breadcrumb={
          <Breadcrumb
            activeLabel={activeBreadcrumbLabel}
            items={[
              {
                label: "Overview",
                onClick: () => navigate("/overview"),
              },
              {
                label: backLabel,
                // Route links leave the open-profile URL cache alive long
                // enough for the profile URL-sync effect to reopen a stale
                // snapshot. Close through the parent instead so the next
                // company profile is seeded from the current table row.
                ...(typeof onBack === "function" ? { onClick: onBack } : { path: "/companies" }),
              },
              {
                label: activeCompany?.name || activeCompany?.company_name || "",
              },
            ]}
          />
        }
        
        statsSection={
          <StatsSlider
            items={stats}
            autoPlay={false}
            showArrows={true}
            cardMinWidth={200}
            gap={12}
            onItemClick={handleStatItemClick}
            activeItemId={tab}
          />
        }
        headerSection={
          <CompanyProfileHeader
            company={activeCompany}
            tab={tab}
            onTabChange={handleTabChange}
            counts={counts}
            summaryRow={
              <CompanyHeader
                company={activeCompany}
                leadId={resolvedLeadEntityId}
                companyId={resolvedCompanyEntityId}
                onLogoUpdated={handleCompanyLogoUpdated}
                sx={{ mt: 0.15, mb: 0 }}
              />
            }
            newAction={statusValue}
            onNewActionChange={handleStatusChange}
            dateText={dateText}
            metaValue={
              activeCompany?.team_member ||
              activeCompany?.owner_name ||
              activeCompany?.owner
            }
            actionLabel="Status"
            actionPlaceholder={isCompanyEntity ? "Company Status" : "Client Status"}
            actionOptions={
              (isCompanyEntity ? COMPANY_STATUS_OPTIONS : CLIENT_STATUS_OPTIONS).map((status) => ({
                value: status,
                label: status,
              }))
            }
            actionDisabled={statusSaving}
            actionFeedback={statusToast}
            onActionFeedbackClose={() =>
              setStatusToast((prev) => ({ ...prev, open: false }))
            }
            sx={isCompanyEntity ? TAB_VIEW_HEADER_SX : undefined}
            tabs={companyHeaderTabs}
          />
        }
        contentWrapperSx={tab === "details" ? TAB_VIEW_CONTENT_WRAPPER_SX : undefined}
        contentSection={
          <>
            <EntityBackgroundTasks
              entity={activeCompany}
              sx={
                tab === "details"
                  ? undefined
                  : {
                      // Non-details tab content supplies its own section padding.
                      // Align the feedback panel and its copy to those section edges.
                      mx: TAB_VIEW_SIDE_PADDING_X,
                      px: 0,
                    }
              }
            />

            {tab === "details" ? (
              <Box>
                <Card sx={{ p: 0, borderRadius: 0, mb: 0, boxShadow: "none" }}>
                  <CompanyDetailsTabContent
                    leadId={isCompanyEntity ? null : resolvedLeadEntityId}
                    companyId={resolvedCompanyEntityId}
                    lead={activeCompany}
                    company={activeCompany}
                    entityType={entityType}
                    onCompanyChange={handleCompanyDetailsChange}
                    fieldOptions={detailFieldOptions}
                  />
                </Card>
              </Box>
            ) : null}

            {tab === "leads" ? (
              <Box>
                <Card sx={{ p: 0, borderRadius: 0, mb: 0, boxShadow: "none" }}>
                  <CompanyLeadsSection
                    companyId={resolvedCompanyEntityId}
                    company={activeCompany}
                    reloadKey={leadReloadKey}
                    onAddNewLead={handleAddNewLead}
                    onSearchLead={handleSearchNewLead}
                    onLeadsChange={handleCompanyLeadsChange}
                    onOpenLeadDetails={onOpenLeadDetails}
                  />
                </Card>
              </Box>
            ) : null}

            {tab === "contacts" ? (
              <Box>
                <Card sx={{ p: 0, borderRadius: 0, mb: 0, boxShadow: "none" }}>
                  <CompanyContactsTabContent
                    leadId={resolvedLeadEntityId}
                    companyId={resolvedCompanyEntityId}
                    lead={activeCompany}
                    reloadKey={contactsReloadKey}
                    company={activeCompany}
                    onAddNewLead={handleAddNewLead}
                    onAddNewJob={handleQuickActionAddJob}
                    onContactsChange={handleCompanyContactsChange}
                    onVisibleContactsCountChange={setVisibleContactsCount}
                  />
                </Card>
              </Box>
            ) : null}

            {tab === "jobs" ? (
              <Box>
                <Card sx={{ p: 0, borderRadius: 0, mb: 0, boxShadow: "none" }}>
                  <CompanyJobsTabContent
                    leadId={resolvedLeadEntityId}
                    companyId={resolvedCompanyEntityId}
                    company={activeCompany}
                    reloadKey={jobsReloadKey}
                    onJobsChange={handleCompanyJobsChange}
                    onAddNewJob={handleQuickActionAddJob}
                  />
                </Card>
              </Box>
            ) : null}

            {tab === "candidates" ? (
              <Box>
                <Card sx={{ borderRadius: 0, mb: 0, boxShadow: "none", ...TAB_VIEW_CONTENT_WRAPPER_SX }}>
                  <CompanyCandidatesTabContent
                    company={activeCompany}
                    leadId={isCompanyEntity ? resolvedCompanyEntityId : resolvedLeadEntityId}
                    entityType={entityType}
                  />
                </Card>
              </Box>
            ) : null}

            {tab === "activities" ? (
              <Box>
                <Card sx={{ p: 0, borderRadius: 0, mb: 0, boxShadow: "none" }}>
                  <LeadActivitiesTabContent
                    activities={activeCompany?.activities || []}
                    company={activeCompany}
                    entityType={entityType}
                    entityId={isCompanyEntity ? resolvedCompanyEntityId : activeCompany?.id}
                    onActivityChange={handleActivitiesChanged}
                  />
                </Card>
              </Box>
            ) : null}
          </>
        }
        rightSidebar={
          <>
            <QuickActionsCard actionItems={companyQuickActionItems} />

            <AIAgentCard
              companySeed={aiCompanySeed}
              currentCompanyId={resolvedCompanyEntityId}
              currentCompanyName={
                activeCompany?.company_name || activeCompany?.name || ""
              }
              entityType={isCompanyEntity ? "company" : "lead"}
              entityId={detailEntityId}
              entityName={activeCompany?.company_name || activeCompany?.name || ""}
            />

            <FundingFinancialsCard
              apiBase={API_BASE}
              companyId={resolvedCompanyEntityId}
              companyName={
                activeCompany?.company_name ||
                activeCompany?.companyName ||
                activeCompany?.name ||
                ""
              }
              summaryItems={hiringManagerSummaryItems}
              initialFunding={{
                funding_stage: activeCompany?.funding_stage,
                last_funding_round_amount: activeCompany?.last_funding_round_amount,
                total_raised_to_date: activeCompany?.total_raised_to_date,
                revenue_estimate: activeCompany?.revenue_estimate,
              }}
              onCompanyChange={isCompanyEntity ? handleCompanyDetailsChange : undefined}
            />

            <HiringTeamCard
              apiBase={API_BASE}
              entityType={entityType}
              entityId={isCompanyEntity ? resolvedCompanyEntityId : activeCompany?.id}
              source={activeCompany?.source || "LinkedIn"}
              summaryItems={hiringManagerSummaryItems}
              onEditAll={() => console.log("edit all")}
            />
          </>
        }
        rightWidth={440}
      />

      <LeadDescriptionDialog
        open={leadDialogOpen}
        onClose={() => setLeadDialogOpen(false)}
        value={leadDialogValue}
        onChange={setLeadDialogValue}
        mode={leadDialogMode}
        initialScreen={leadDialogInitialScreen}
        onSubmit={() => {
          refreshQuickActionData();

          // Stay in the company profile and show the collection that changed.
          // The dialog's default behavior navigates to a newly-created lead,
          // which unmounts this refreshed profile before users can see it.
          handleTabChange(leadDialogMode === "contact" ? "contacts" : "leads");

          return false;
        }}
      />

      <AddJobDialog
        open={addJobOpen}
        onClose={() => setAddJobOpen(false)}
        onCreated={handleQuickActionJobCreated}
        initialScreen="wizard"
        initialWizardStep={0}
        initialValues={addJobInitialValues}
        createButtonLabel="Add Job"
        variant="companyQuickAction"
      />

      <AddToSequenceDialog
        open={sequenceDialogOpen}
        onClose={() => setSequenceDialogOpen(false)}
        entityType={isCompanyEntity ? "company" : "lead"}
        items={[
          {
            entityType: isCompanyEntity ? "company" : "lead",
            entityId: detailEntityId,
            name: String(
              activeCompany?.company_name || activeCompany?.companyName || activeCompany?.name || ""
            ).trim(),
            company: String(
              activeCompany?.company_name || activeCompany?.companyName || activeCompany?.name || ""
            ).trim(),
            email: preferredCompanyEmail,
            phone: preferredCompanyPhone,
          },
        ].filter((item) => Boolean(item.entityId))}
        onCreated={() => {
          setSequenceDialogOpen(false);
          refreshQuickActionData();
        }}
      />

      <AIAgentDialog
        open={proposalDialogOpen}
        onClose={() => {
          setProposalDialogOpen(false);
          setProposalDialogPrompt("");
          setProposalDialogSubmitOnOpen(false);
        }}
        onSend={handleQuickActionProposalSubmitted}
        initialPrompt={proposalDialogPrompt}
        submitOnOpen={proposalDialogSubmitOnOpen}
      />
    </>
  );
}
